import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import { Team } from 'Src/models/team';
import { getKitLabKits, KitLabBackendKit } from 'Src/helpers/service/admin/kitLab';
import { getKitGenderForTeam } from 'Src/helpers/kitLab/kitLab';
import { Loader } from 'Src/components/Loader/Loader';
import { KitShirtPreview } from 'Src/components/KitLab/KitShirtPreview';
import { KitShortsPreview } from 'Src/components/KitLab/KitShortsPreview';
import { KitSkortPreview } from 'Src/components/KitLab/KitSkortPreview';
import {
  KitBottomPattern,
  Gender,
  DetailColorSource,
  SleevePattern,
  KitPatternVariant,
  ShortsSideTrianglesPosition
} from 'Src/types/kitLab';
import './KitSelector.scss';

interface LayoutPresetObj {
  shirtPattern: KitPatternVariant;
  bottomPattern: KitBottomPattern;

  primaryColor: string;
  secondaryColor: string;
  shortsPrimaryColor: string;
  shortsSecondaryColor: string;

  showPocket: boolean;
  showCollar: boolean;
  showSleeves: boolean;
  showShoulderTriangles: boolean;
  showSleeveCuffs: boolean;

  pocketColorSource: DetailColorSource;
  sleevesColorSource: DetailColorSource;
  collarColorSource: DetailColorSource;
  shoulderTrianglesColorSource: DetailColorSource;
  sleeveCuffsColorSource: DetailColorSource;

  pocketCustomColor: string;
  sleevesCustomColor: string;
  collarCustomColor: string;
  shoulderTrianglesCustomColor: string;
  sleeveCuffsCustomColor: string;

  collarStyle: 'ROUND' | 'POLO';

  stripeCount: number;
  stripesEqualWidth: boolean;
  stripePattern: number[];

  skortStripeCount: number;
  skortStripesEqualWidth: boolean;
  skortStripePattern: number[];

  diagonalDirection: 'LEFT' | 'RIGHT';
  chevronSize: 'SMALL' | 'BIG';
  chevronDirection: 'UP' | 'DOWN';
  chevronPosition: 'TOP' | 'MIDDLE' | 'BOTTOM';
  sideTrianglesMode: 'TOP' | 'BOTTOM' | 'BOTH';
  skortSideTrianglesMode: 'TOP' | 'BOTTOM' | 'BOTH';
  shortsTrianglesMode: ShortsSideTrianglesPosition;
  sleevePattern: SleevePattern;
}

const DEFAULT_LAYOUT: LayoutPresetObj = {
  shirtPattern: 'plain',
  bottomPattern: 'SHORTS_PLAIN',

  primaryColor: '#1f78ff',
  secondaryColor: '#ffd24d',
  shortsPrimaryColor: '#1f78ff',
  shortsSecondaryColor: '#ffd24d',

  showPocket: true,
  showCollar: true,
  showSleeves: true,
  showShoulderTriangles: false,
  showSleeveCuffs: false,

  pocketColorSource: 'SECONDARY',
  sleevesColorSource: 'SECONDARY',
  collarColorSource: 'SECONDARY',
  shoulderTrianglesColorSource: 'SECONDARY',
  sleeveCuffsColorSource: 'SECONDARY',

  pocketCustomColor: '#ffffff',
  sleevesCustomColor: '#ffffff',
  collarCustomColor: '#ffffff',
  shoulderTrianglesCustomColor: '#ffffff',
  sleeveCuffsCustomColor: '#ffffff',

  collarStyle: 'ROUND',

  stripeCount: 5,
  stripesEqualWidth: true,
  stripePattern: [1, 1, 1, 1, 1],

  skortStripeCount: 5,
  skortStripesEqualWidth: true,
  skortStripePattern: [1, 1, 1, 1, 1],

  diagonalDirection: 'RIGHT',
  chevronSize: 'SMALL',
  chevronDirection: 'DOWN',
  chevronPosition: 'MIDDLE',
  sideTrianglesMode: 'BOTH',
  skortSideTrianglesMode: 'BOTH',
  shortsTrianglesMode: 'BOTH',
  sleevePattern: 'PLAIN'
};

function isSkortPatternId(pattern?: KitBottomPattern): boolean {
  if (!pattern) return false;
  return (
    pattern === 'SKORT_PLAIN' ||
    pattern === 'SKORT_VERTICAL_STRIPES' ||
    pattern === 'SKORT_SIDE' ||
    pattern === 'SKORT_TRIANGLES' ||
    pattern === 'SKORT_BOTTOM_TRIANGLES' ||
    pattern === 'SKORT_SEMICIRCLES'
  );
}

function mapBottomToSkortPattern(
  pattern: KitBottomPattern | undefined
): 'plain' | 'verticalStripes' | 'sideStripes' | 'sideTriangles' | 'sideBottomTriangles' | 'sideSemiCircles' {
  switch (pattern) {
    case 'SKORT_VERTICAL_STRIPES':
      return 'verticalStripes';
    case 'SKORT_SIDE':
      return 'sideStripes';
    case 'SKORT_TRIANGLES':
      return 'sideTriangles';
    case 'SKORT_BOTTOM_TRIANGLES':
      return 'sideBottomTriangles';
    case 'SKORT_SEMICIRCLES':
      return 'sideSemiCircles';
    case 'SKORT_PLAIN':
    default:
      return 'plain';
  }
}

function resolveDetailColor(source: DetailColorSource, custom: string, primary: string, secondary: string): string {
  if (source === 'PRIMARY') return primary;
  if (source === 'SECONDARY') return secondary;
  return custom || secondary || primary;
}

function getLayoutForKit(kit: KitLabBackendKit): LayoutPresetObj {
  let base: LayoutPresetObj = { ...DEFAULT_LAYOUT };

  if (kit.layoutPreset) {
    try {
      const parsed = JSON.parse(kit.layoutPreset);
      if (parsed && typeof parsed === 'object') {
        base = { ...base, ...(parsed as Partial<LayoutPresetObj>) };
      }
    } catch (e) {}
  } else if (Array.isArray(kit.segments)) {
    const primarySeg = kit.segments.find(s => s.segmentKey === 'SHIRT_PRIMARY');
    const secondarySeg = kit.segments.find(s => s.segmentKey === 'SHIRT_SECONDARY');
    const shortsPrimarySeg = kit.segments.find(s => s.segmentKey === 'SHORTS_PRIMARY');
    const shortsSecondarySeg = kit.segments.find(s => s.segmentKey === 'SHORTS_SECONDARY');

    base.primaryColor = primarySeg?.color || base.primaryColor;
    base.secondaryColor = secondarySeg?.color || base.secondaryColor;
    base.shortsPrimaryColor = shortsPrimarySeg?.color || base.shortsPrimaryColor;
    base.shortsSecondaryColor = shortsSecondarySeg?.color || base.shortsSecondaryColor;
  }

  return base;
}

interface Props {
  user: AppUser;
  team: Team;
  onSelect: (kit: KitLabBackendKit) => void;
  onClear: () => void;
  onCancel: () => void;
  selectedKitId?: string | null;
}

export const KitSelector: React.FC<Props> = ({ user, team, onSelect, onClear, onCancel, selectedKitId }) => {
  const [allKits, setAllKits] = React.useState<KitLabBackendKit[]>([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [search, setSearch] = React.useState<string>('');
  const [appliedSearch, setAppliedSearch] = React.useState<string>('');

  const explicitSelectedId =
    selectedKitId !== undefined && selectedKitId !== null ? selectedKitId.toString() : undefined;

  const teamKitId = team && (team as any).kitLabKitId ? (team as any).kitLabKitId.toString() : undefined;

  const currentKitId = explicitSelectedId || teamKitId;

  React.useEffect(() => {
    let isMounted = true;

    const loadKits = async () => {
      setIsLoading(true);
      try {
        const kits = await getKitLabKits(user);
        if (!isMounted) return;
        setAllKits(kits || []);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    loadKits();

    return () => {
      isMounted = false;
    };
  }, [user, team]);

  const filteredKits = React.useMemo(() => {
    if (!team) return [];

    const teamSportId = (team as any).sportId ? (team as any).sportId.toString() : undefined;
    const teamKitGender = getKitGenderForTeam((team as any).gender);

    return (allKits || [])
      .filter(kit => {
        const kitGenderRaw = (kit.gender || '').toString().toUpperCase() as Gender | '' | undefined;
        const teamGenderRaw = (teamKitGender || '').toString().toUpperCase();

        const genderOk = !kitGenderRaw || !teamGenderRaw || kitGenderRaw === teamGenderRaw;

        if (!genderOk) return false;

        const sportIds = Array.isArray(kit.sportIds) ? kit.sportIds.map(id => id && id.toString()) : [];

        if (!teamSportId) return true;

        if (sportIds.length === 0) return true;

        return sportIds.includes(teamSportId);
      })
      .filter(kit => {
        if (!appliedSearch) return true;
        const name = (kit.name || '').toLowerCase();
        return name.includes(appliedSearch.toLowerCase());
      });
  }, [allKits, team, appliedSearch]);

  const renderKitCard = (kit: KitLabBackendKit) => {
    const layout = getLayoutForKit(kit);

    const isDefault = !!kit.isDefault;
    const kitGenderRaw = (kit.gender || '').toString().toUpperCase();
    const genderText = kitGenderRaw === 'MALE' ? 'Boys' : kitGenderRaw === 'FEMALE' ? 'Girls' : 'Any team';

    const hasSports = Array.isArray(kit.sportIds) && kit.sportIds.length > 0;
    const sportsText = hasSports ? 'Sport-specific' : 'All sports';

    const isCurrent = currentKitId && kit._id && currentKitId === kit._id.toString();

    const pocketColor = resolveDetailColor(
      layout.pocketColorSource,
      layout.pocketCustomColor,
      layout.primaryColor,
      layout.secondaryColor
    );
    const sleevesColor = resolveDetailColor(
      layout.sleevesColorSource,
      layout.sleevesCustomColor,
      layout.primaryColor,
      layout.secondaryColor
    );
    const collarColor = resolveDetailColor(
      layout.collarColorSource,
      layout.collarCustomColor,
      layout.primaryColor,
      layout.secondaryColor
    );
    const shoulderTrianglesColor = resolveDetailColor(
      layout.shoulderTrianglesColorSource,
      layout.shoulderTrianglesCustomColor,
      layout.primaryColor,
      layout.secondaryColor
    );
    const sleeveCuffsColor = resolveDetailColor(
      layout.sleeveCuffsColorSource,
      layout.sleeveCuffsCustomColor,
      layout.primaryColor,
      layout.secondaryColor
    );

    const isSkort = isSkortPatternId(layout.bottomPattern);

    return (
      <button
        key={kit._id}
        type="button"
        className={'eKitSelector__card' + (isCurrent ? ' eKitSelector__card--current' : '')}
        onClick={() => onSelect(kit)}
      >
        <div className="eKitSelector__cardInner">
          <div className="eKitSelector__preview">
            <KitShirtPreview
              variant={layout.shirtPattern}
              primaryColor={layout.primaryColor}
              secondaryColor={layout.secondaryColor}
              showPocket={layout.showPocket}
              showCollar={layout.showCollar}
              showSleeves={layout.showSleeves}
              showShoulderTriangles={layout.showShoulderTriangles}
              showSleeveCuffs={layout.showSleeveCuffs}
              pocketColor={pocketColor}
              sleevesColor={sleevesColor}
              collarColor={collarColor}
              shoulderTrianglesColor={shoulderTrianglesColor}
              sleeveCuffsColor={sleeveCuffsColor}
              stripeCount={layout.stripeCount}
              stripesEqualWidth={layout.stripesEqualWidth}
              stripePattern={layout.stripePattern}
              diagonalDirection={layout.diagonalDirection}
              chevronSize={layout.chevronSize}
              chevronDirection={layout.chevronDirection}
              chevronPosition={layout.chevronPosition}
              sideTrianglesMode={layout.sideTrianglesMode}
              sleevePattern={layout.sleevePattern}
              collarStyle={layout.collarStyle}
              className="eKitLab__shirtSvg--small"
            />

            {isSkort ? (
              <KitSkortPreview
                pattern={mapBottomToSkortPattern(layout.bottomPattern)}
                primaryColor={layout.shortsPrimaryColor}
                secondaryColor={layout.shortsSecondaryColor}
                stripeCount={layout.skortStripeCount}
                stripesEqualWidth={layout.skortStripesEqualWidth}
                stripePattern={layout.skortStripePattern}
                sideTrianglesMode={layout.skortSideTrianglesMode}
                className="eKitLab__bottomSvg--small eKitLab__bottomSvg--smallSkort"
              />
            ) : (
              <KitShortsPreview
                variant={
                  layout.bottomPattern === 'SHORTS_SIDE'
                    ? 'sideStripes'
                    : layout.bottomPattern === 'SHORTS_TRIANGLES'
                    ? 'sideTriangles'
                    : layout.bottomPattern === 'SHORTS_BIG_TRIANGLES'
                    ? 'sideBigTriangles'
                    : layout.bottomPattern === 'SHORTS_SEMICIRCLES'
                    ? 'sideSemiCircles'
                    : 'plain'
                }
                primaryColor={layout.shortsPrimaryColor}
                secondaryColor={layout.shortsSecondaryColor}
                sideTrianglesPosition={layout.shortsTrianglesMode}
                className="eKitLab__bottomSvg--small"
              />
            )}
          </div>

          <div className="eKitSelector__info">
            <div className="eKitSelector__name">{kit.name || 'Unnamed kit'}</div>
            <div className="eKitSelector__meta">
              {genderText} · {sportsText}
            </div>

            <div className="eKitSelector__badges">
              {isDefault && <span className="eKitSelector__badge eKitSelector__badge--default">Default kit</span>}
              {isCurrent && <span className="eKitSelector__badge eKitSelector__badge--current">Current</span>}
            </div>
          </div>
        </div>
      </button>
    );
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="eKitSelector">
      <div className="mb-3">
        <input
          className="form-control"
          placeholder="Search kits by name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') {
              event.preventDefault();
              setAppliedSearch(search.trim());
            }
          }}
        />
      </div>

      <div className="eKitSelector__grid">
        {filteredKits.map(renderKitCard)}
        {filteredKits.length === 0 && (
          <div className="eKitSelector__empty">
            No suitable kits found for this team. Try changing sports / gender for the kit in Kit Lab, or create a new
            one.
          </div>
        )}
      </div>

      <div className="eKitSelector__footer">
        <button type="button" className="btn btn-link" onClick={onCancel}>
          Cancel
        </button>
        <div className="ml-auto">
          <button type="button" className="btn btn-secondary mr-2" onClick={onClear}>
            Remove kit
          </button>
        </div>
      </div>
    </div>
  );
};
